import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { isNil } from "ramda";

import { useFacebookActions } from "store/FacebookSlice";
import {
  init,
  login,
  getAllAlbums,
  getAllUserInfo,
  getImagesOfAlbum,
  getLoginStatus,
  getNextPage,
  validatePermissions,
} from "utils/facebookUtils";
import { jjLogger } from "utils/logUtils";

import useSettings from "./useSettings";
import useScreen from "./useScreen";

const useFacebook = (initialize = false, isOauth = false) => {
  const {
    loadAlbums,
    loadUserInfo,
    loadPhotos,
    loadAdditionalPhotos,
    loadAdditionalAlbums,
    changeMode,
    changeModalOpen,
    changeSelectedPhoto,
    changeInitialized,
    clearPhotos,
    clearSelectedPhoto,
    resetMode,
    clearAlbums,
    changeLoading,
    changeNoticeModal,
  } = useFacebookActions();
  const [nextPage, setNextPage] = useState(null);
  const {
    settings: { fbAppId },
  } = useSettings();
  const { isDesktop } = useScreen();

  const { albums, mode, photos, modalOpen, selectedPhoto, initialized, showNoticeModal } = useSelector(
    (state) => state.FacebookSlice,
  );
  const loading = useSelector((state) => (isOauth ? true : state.FacebookSlice.loading));

  useEffect(() => {
    if (initialize && !initialized) {
      init(fbAppId, changeInitialized).catch((error) => {
        changeLoading(false);
        jjLogger.logError(`useFacebook.js: init(): error initializing facebook sdk - error: ${JSON.stringify(error)}`);
      });
    }
  }, []);

  const changeModalOpenStorage = (value) => {
    changeModalOpen(value);
  };

  const availableAlbums = albums?.filter((album) => !isNil(album?.cover_photo?.images));

  const hasSelectedPhoto = !isNil(selectedPhoto) && !isNil(selectedPhoto?.source);

  const checkLoggedinStatus = () => {
    return getLoginStatus()
      .then((response) => validatePermissions(response.status))
      .then(() => changeLoading(false))
      .catch((error) => {
        changeLoading(false);
        if (!error || error.message !== "Facebook user does not seem connected") {
          jjLogger.logError(
            `useFacebook - checkLoggedinStatus() - error getting login status of user: ${JSON.stringify(error)}`,
          );
        }
      });
  };

  const changeNoticeModalOpen = (value) => {
    if (isDesktop) {
      changeNoticeModal(value);
    }
  };

  const getUserInfo = () => {
    return getAllUserInfo()
      .then((response) => {
        loadUserInfo(response);
        return response;
      })
      .catch((error) => {
        jjLogger.logError(`useFacebook.js: getUserInfo(): error getting user info: ${JSON.stringify(error)}`);
      });
  };

  const loginFacebook = () => {
    changeLoading(true);
    changeNoticeModalOpen(true);

    return login()
      .then((response) => {
        changeNoticeModalOpen(false);
        return response.authResponse;
      })
      .catch((error) => {
        changeNoticeModalOpen(false);
        changeLoading(false);
        jjLogger.logError(
          `useFacebook.js: loginFacebook(): error when logging in with facebook: ${JSON.stringify(error)}`,
        );
      });
  };

  const getAlbums = () => {
    return getAllAlbums(setNextPage)
      .then(loadAlbums)
      .catch(() => {
        jjLogger.logError("useFacebook.js: getAlbums(): error getting user albums");
      });
  };

  const getImages = (albumId) => {
    return getImagesOfAlbum(albumId, setNextPage)
      .then(loadPhotos)
      .catch(() => {
        jjLogger.logError("useFacebook.js: getImages(): error getting photos of album");
      });
  };

  const loadNextPage = (onLoaded) => {
    if (!isNil(nextPage)) {
      return getNextPage(nextPage, setNextPage)
        .then(onLoaded)
        .catch(() => {
          jjLogger.logError("useFacebook.js: loadNextPage(): error getting next page of albums or image");
        });
    }
    return null;
  };

  return {
    albums,
    mode,
    photos,
    initialized,
    changeMode,
    changeModalOpenStorage,
    modalOpen,
    availableAlbums,
    selectedPhoto,
    loading,
    changeSelectedPhoto,
    hasSelectedPhoto,
    loginFacebook,
    loadNextPage,
    loadAdditionalPhotos,
    loadAdditionalAlbums,
    getAlbums,
    getUserInfo,
    getImages,
    checkLoggedinStatus,
    resetMode,
    clearPhotos,
    clearSelectedPhoto,
    clearAlbums,
    changeNoticeModalOpen,
    showNoticeModal,
  };
};

export default useFacebook;
